import { useTranslation, untranslated } from "@i18n";
import { useState } from "react";
import Signature, { type SignatureDocument } from "@Components/Signature";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { SignTypeValues } from "@CubeComponents/constants";
import { addSignatureToResources } from "./ReviewsHelper";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import { RootState } from "@Reducers/index";
import type { AddSignatureToResourcesRequest } from "@ServerTypes/reviews/reviews.schema";

interface AddSignatureToResourceProps {
  readonly closeModal: (refresh?: boolean) => void;
  /** Sent verbatim as `addSignatureToResourcesRequest.resources`. */
  readonly resources: AddSignatureToResourcesRequest["resources"];
  readonly reviewId: AddSignatureToResourcesRequest["reviewId"];
}

/**
 * `Signature`'s `onSuccess` fires with EITHER the newly picked sign-type string (its dropdown, or
 * `undefined` when it is cleared) or the captured document (draw / attach). Both reach
 * `handleSignDocChange`, so this mirrors `SignatureProps["onSuccess"]` exactly.
 */
type SignatureSuccessPayload = string | SignatureDocument | undefined;

interface SignatureModalData {
  signDoc: string | null | undefined;
  signType: string;
}

function AddSignatureToResource({ closeModal, resources, reviewId }: AddSignatureToResourceProps) {
  const { t } = useTranslation();
  const defaultModalData: SignatureModalData = {
    signDoc: null,
    signType: SignTypeValues.DRAW_ON_SCREEN,
  };
  const user = useSelector((state: RootState) => state.userDetails);

  const dispatch = useDispatch();

  const [modalData, setModalData] = useState(defaultModalData);
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const [isLoadingForSignature, setIsLoadingForSignature] = useState(false);

  const confirmHandler = async () => {
    setIsLoadingForSignature(true);
    const data = {
      resources,
      reviewId,
      signature: modalData.signDoc || undefined,
      ...(modalData.signDoc ? {} : { signatureId: user.signature }),
    };

    const response = await addSignatureToResources(data);
    setIsLoadingForSignature(false);
    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: t("The signature has been added to the selected resources"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      closeModal(true);
      return;
    }

    const content = response.data.errorMessage
      ? untranslated(response.data.errorMessage)
      : untranslated("The signature could not be added");

    dispatch(showToastNotification({ content, type: TOAST_TYPE_ERROR }));
    closeModal();
  };

  const handleSignDocChange = (e: SignatureSuccessPayload) => {
    if (e === SignTypeValues.IMPORT_PROFILE_SIGNATURE) {
      if (user.signature) {
        setIsButtonEnabled(true);
      }
    }
    // Only the draw / attach branch carries a document; the sign-type-string branch clears `signDoc`,
    // which is what re-disables Confirm when the user switches type.
    const signDoc = typeof e === "object" ? e.base64ImageStr : undefined;
    setModalData((prev) => ({ ...prev, signDoc }));
  };

  const header = () => (
    <p className="font-[var(--font-weight-600)] text-2xl w-full rounded-t-1">{t("Add Signature")}</p>
  );

  const content = () => (
    <div className="flex flex-col gap-6 h-[31.25rem]">
      <div className="flex flex-row items-start justify-between me-2 gap-24">
        <span>{t("Sign")}</span>
        <div className="w-[27.875rem]">
          <Signature
            onSuccess={handleSignDocChange}
            dataSource={[
              {
                name: "Draw On Screen",
                value: SignTypeValues.DRAW_ON_SCREEN,
              },
              {
                name: "Attach Signed Document",
                value: SignTypeValues.ATTACH_SIGNED_DOCUMENT,
              },
              {
                name: "Add From Profile Settings",
                value: SignTypeValues.IMPORT_PROFILE_SIGNATURE,
              },
            ]}
            defaultSelectedSignType={SignTypeValues.DRAW_ON_SCREEN}
          />
        </div>
      </div>
    </div>
  );

  const footer = () => (
    <div className="flex gap-4">
      <ButtonComponent
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        onClick={confirmHandler}
        disabled={!modalData.signDoc && !isButtonEnabled}
        loading={isLoadingForSignature}
      >
        {t("Confirm")}
      </ButtonComponent>
      <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={() => closeModal()}>
        {t("Cancel")}
      </ButtonComponent>
    </div>
  );

  return (
    <ModalStateDialogComponent
      allowModalPadding={false}
      headerTitle={header()}
      modelContent={content()}
      footerContent={footer()}
      isVisible
      onCancel={closeModal}
      width="42.5rem"
    />
  );
}

export default AddSignatureToResource;
